/**
 * AuditLogViewer — read-only security/audit review panel (Phase E).
 *
 * Role gating: super_admin + crm_admin only. Uses existing /admin/audit-log
 * API. Surfaces:
 *   • event-type dropdown (from /admin/audit-log/actions)
 *   • actor + target user id filters
 *   • since-hours quick filter (1 / 24 / 168 / all)
 *   • load-more pagination
 *   • lockout-related events highlighted
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Search, ShieldAlert, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCKOUT_ACTIONS = new Set([
  "lockout_started", "lockout_cleared", "login_failed", "mfa.verify_failed", "rate_limit_triggered",
]);

export default function AuditLogViewer({ role }) {
  const allowed = role === "super_admin" || role === "crm_admin";
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [actorId, setActorId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [sinceHours, setSinceHours] = useState("24");
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/admin/audit-log/actions`, { headers: { Authorization: `Bearer ${token}` } });
        setActions(res.data?.actions || []);
      } catch {
        setActions([]);
      }
    })();
  }, [allowed]);

  const fetchEvents = async (append = false) => {
    if (!allowed) return;
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      if (actorId) params.set("actor_id", actorId);
      if (targetId) params.set("target_id", targetId);
      if (sinceHours) params.set("since_hours", sinceHours);
      params.set("limit", "100");
      params.set("offset", append ? String(events.length) : "0");
      const res = await axios.get(`${API}/admin/audit-log?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const page = res.data?.events || [];
      setEvents(append ? [...events, ...page] : page);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchEvents reads fresh state via closure; only filter changes should retrigger
  useEffect(() => { fetchEvents(false); }, [action, actorId, targetId, sinceHours]);

  if (!allowed) {
    return <div data-testid="audit-log-forbidden" style={{ padding: 20, background: "#EEF4FF", color: "#2563eb", borderRadius: 8, fontSize: 12 }}>
      This view is restricted to super_admin and crm_admin roles.
    </div>;
  }

  return (
    <div data-testid="audit-log-viewer">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 650, display: "flex", alignItems: "center", gap: 6 }}>
            <ShieldAlert style={{ width: 14, height: 14, color: "#2563eb" }} /> Security & audit log
          </div>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
            Authentication, MFA, lockout, invite, and permission-override events — read-only.
          </div>
        </div>
        <button data-testid="audit-log-refresh" onClick={() => fetchEvents(false)} className="btn" style={{ fontSize: 12 }}>
          <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <select data-testid="audit-log-filter-action" className="input" value={action} onChange={e => setAction(e.target.value)} style={{ fontSize: 12, minWidth: 200 }}>
          <option value="">All event types</option>
          <option value="prefix:mfa.">All MFA events</option>
          <option value="prefix:invite.">All invite events</option>
          <option value="prefix:permissions.">All permission events</option>
          <option value="lockout_started">lockout_started</option>
          <option value="lockout_cleared">lockout_cleared</option>
          <option value="login_failed">login_failed</option>
          <option value="rate_limit_triggered">rate_limit_triggered</option>
          {actions.filter(a => !["mfa.verify_failed", "invite.accepted", "invite.created"].includes(a)).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="audit-log-filter-since" className="input" value={sinceHours} onChange={e => setSinceHours(e.target.value)} style={{ fontSize: 12, minWidth: 140 }}>
          <option value="1">Last hour</option>
          <option value="24">Last 24 hours</option>
          <option value="168">Last 7 days</option>
          <option value="">All time</option>
        </select>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} />
          <input data-testid="audit-log-filter-actor" className="input" placeholder="Actor id…" value={actorId} onChange={e => setActorId(e.target.value)} style={{ paddingLeft: 26, fontSize: 12 }} />
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} />
          <input data-testid="audit-log-filter-target" className="input" placeholder="Target id…" value={targetId} onChange={e => setTargetId(e.target.value)} style={{ paddingLeft: 26, fontSize: 12 }} />
        </div>
      </div>

      {err && <div data-testid="audit-log-error" style={{ padding: 10, background: "#FFEBEB", color: "#A32D2D", borderRadius: 8, fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <div data-testid="audit-log-table" style={{ border: "0.5px solid var(--border)", borderRadius: 8, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead style={{ background: "var(--page-bg)" }}>
            <tr>
              <th style={th()}>Time (UTC)</th>
              <th style={th()}>Action</th>
              <th style={th()}>Actor</th>
              <th style={th()}>Target</th>
              <th style={th()}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} data-testid={`audit-log-row-${e.id}`} data-action={e.action} style={{ borderBottom: "0.5px solid var(--border)", background: LOCKOUT_ACTIONS.has(e.action) ? "#FFF8F0" : "transparent" }}>
                <td style={td()} title={e.created_at}>{(e.created_at || "").replace("T", " ").split(".")[0]}</td>
                <td style={{ ...td(), fontFamily: "monospace" }}>
                  {LOCKOUT_ACTIONS.has(e.action) && <AlertTriangle style={{ display: "inline", width: 10, height: 10, color: "#854F0B", marginRight: 4 }} />}
                  {e.action}
                </td>
                <td style={td()}><code style={{ fontSize: 10, color: "#666" }}>{e.actor_id || "—"}</code>{e.actor_role && <span style={{ fontSize: 9, color: "#888", marginLeft: 4 }}>({e.actor_role})</span>}</td>
                <td style={td()}><code style={{ fontSize: 10, color: "#666" }}>{e.target_id || "—"}</code></td>
                <td style={{ ...td(), color: "var(--text-tertiary)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {Object.keys(e.extra || {}).length > 0 ? JSON.stringify(e.extra) : "—"}
                </td>
              </tr>
            ))}
            {!loading && events.length === 0 && (
              <tr><td colSpan={5} data-testid="audit-log-empty" style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#888" }}>No events for the selected filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <span data-testid="audit-log-count" style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          Showing {events.length} of {total}
        </span>
        {events.length < total && (
          <button data-testid="audit-log-load-more" className="btn" style={{ fontSize: 12 }} onClick={() => fetchEvents(true)} disabled={loading}>
            {loading ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}

function th() { return { textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", borderBottom: "0.5px solid var(--border)" }; }
function td() { return { padding: "6px 10px", fontSize: 11, color: "var(--text-primary)", verticalAlign: "top" }; }
